import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Theme } from "@mui/material";
import { HierarchyNode, canCopyItem } from "src/pages/configurator/configurator-utils";
import { ProductLineItemRow } from "./product-line-item-row";
import { Model_ConfigurationGet_SystemProductConfigurationItem } from "src/backend/openapi/v1";

type Item = Model_ConfigurationGet_SystemProductConfigurationItem;

type Props = {
  nodes: HierarchyNode<Item>[];
  selectedProjectLineItemId: string | undefined;
  onSelect: (id: string | undefined) => void;
  onAddChild: (item: Item) => void;
  onDeleteItem: (item: Item) => void;
  onCopy: (item: Item, blankValues: boolean, count: number) => void;
  maxCount?: number;
  theme: Theme;
  // 0 = top node (can have multiple children); >0 = child node (can have at most one)
  depth?: number;
};

// Recursively renders one level of the tree as real TreeItems — every sibling is shown
// individually, even when several share the same product name/number.
export const ProductTreeItems = (props: Props) => {
  const { nodes, selectedProjectLineItemId, onSelect, onAddChild, onDeleteItem, onCopy, maxCount, theme, depth = 0 } = props;

  return (
    <>
      {nodes.map(item => {
        const itemId = item.systemProductConfigurationItemId ?? "";
        // Only the top node may have multiple children — a child node can have at most one, so
        // its add button disappears once that single child exists
        const canAddChild = Boolean(item.nextProductTypeName && item.systemProductConfigurationItemId && item.nextProductTypeId && item.systemPackageId) && (depth === 0 || item.children.length === 0);
        const canCopy = canCopyItem(item);

        return (
          <TreeItem
            key={itemId}
            itemId={itemId}
            label={
              <ProductLineItemRow
                item={item}
                isSelected={selectedProjectLineItemId === item.systemProductConfigurationItemId}
                onSelect={() => onSelect(item.systemProductConfigurationItemId)}
                onAddChild={() => onAddChild(item)}
                onDelete={() => onDeleteItem(item)}
                onCopy={(blankValues, count) => onCopy(item, blankValues, count)}
                maxCount={maxCount}
                canAddChild={canAddChild}
                canCopy={canCopy}
                theme={theme}
              />
            }
          >
            {item.children.length > 0 && (
              <ProductTreeItems
                nodes={item.children}
                selectedProjectLineItemId={selectedProjectLineItemId}
                onSelect={onSelect}
                onAddChild={onAddChild}
                onDeleteItem={onDeleteItem}
                onCopy={onCopy}
                maxCount={maxCount}
                theme={theme}
                depth={depth + 1}
              />
            )}
          </TreeItem>
        );
      })}
    </>
  );
};
